import React, { useState, useMemo } from 'react';
import OnboardingTooltip from './OnboardingTooltip';
import './CalendarView.css';
import { getLocalDateString } from '../constants';

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function CalendarView({ onToggleBreakDay, forecastMap, subjectMapDerived, docket, isMixMode, allItemsMap, userGoals, seenHints, onDismissHint }) {
    const [viewDate, setViewDate] = useState(new Date());
    const todayStr = getLocalDateString(new Date());

    const historyMap = useMemo(() => {
        const map = new Map();
        if (docket?.schedule) {
            docket.schedule.forEach(day => map.set(day.date, day));
        }
        return map;
    }, [docket]);

    const calendarWeeks = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDay = new Date(year, month, 1);
        
        const weeks = [];
        let currentDay = new Date(firstDay);
        currentDay.setDate(currentDay.getDate() - currentDay.getDay()); 

        for (let i = 0; i < 42; i++) {
            if (i % 7 === 0) weeks.push([]);
            const dateStr = getLocalDateString(currentDay);
            const projection = forecastMap.get(dateStr);
            const history = historyMap.get(dateStr);
            const isToday = dateStr === todayStr;
            const isPast = dateStr < todayStr;
            const isMasteryGoal = dateStr === userGoals?.target_mastery_date;
            const isBarExam = dateStr === userGoals?.bar_exam_date;

            let dayIsBreak = false;
            if (history && typeof history.is_break_day === 'boolean') dayIsBreak = history.is_break_day;
            else if (projection) dayIsBreak = projection.isBreak;

            let resolvedSubject = 'break';
            if (!dayIsBreak) {
                const isWorkDone = history?.metrics?.learned_count > 0 || history?.metrics?.quiz_count > 0;
                
                if (isPast) {
                    // Past: Show what actually happened
                    if (isWorkDone) resolvedSubject = 'mixed'; // Or derived from history phases if available
                    else resolvedSubject = 'break'; // Missed day
                } else if (isToday) {
                    // Today: Show Plan UNLESS work is already done
                    if (isWorkDone) {
                         resolvedSubject = 'mixed'; 
                    } else {
                         // SHOW THE PLAN!
                         resolvedSubject = projection?.projectedSubject || (isMixMode ? 'mixed' : 'break');
                    }
                } else {
                    // Future: Show Plan
                    resolvedSubject = projection?.projectedSubject || (isMixMode ? 'mixed' : 'break');
                }
                
                // Specific fix for Linear Mode visualization in history
                // If we have phase data, try to be specific instead of generic 'mixed'
                if ((isPast || (isToday && isWorkDone)) && history?.phases) {
                     const learnPhase = history.phases.find(p => p.phase_type === 'LEARN_NEW_RULES');
                     if (learnPhase && learnPhase.item_ids?.length > 0) {
                         const firstRuleId = learnPhase.item_ids[0];
                         const ruleObj = allItemsMap.get(firstRuleId);
                         if (ruleObj) resolvedSubject = ruleObj.subject;
                     }
                }
            }

            // Streak Logic Calculation
            let streakStatus = 'none'; // 'none', 'active', 'frozen'
            if (isPast || isToday) {
                const m = history?.metrics;
                const hasActivity = m && (m.learned_count > 0 || m.quiz_count > 0 || (m.review_count || 0) > 0);
                
                if (hasActivity) {
                    streakStatus = 'active';
                } else if (dayIsBreak) {
                    streakStatus = 'frozen';
                }
            }

            weeks[weeks.length - 1].push({
                dateObj: new Date(currentDay),
                dateStr,
                isCurrentMonth: currentDay.getMonth() === month,
                isToday,
                isPast,
                isBreak: dayIsBreak,
                isMasteryGoal,
                isBarExam,
                resolvedSubject: resolvedSubject,
                projection: projection,
                history: history,
                streakStatus: streakStatus
            });
            currentDay.setDate(currentDay.getDate() + 1);
        }
        return weeks;
    }, [viewDate, forecastMap, historyMap, todayStr, allItemsMap, isMixMode, userGoals]);

    const renderSubjectSpans = (week) => {
        if (isMixMode) return null;
        const spans = [];
        let currentSpan = null;

        week.forEach((day, index) => {
            const subj = day.resolvedSubject;
            const isBreak = day.isBreak || subj === 'break';
            if (!currentSpan || currentSpan.subject !== subj) {
                if (currentSpan) spans.push(currentSpan);
                currentSpan = { subject: subj, isBreak: isBreak, start: index, length: 1 };
            } else {
                currentSpan.length++;
            }
        });
        if (currentSpan) spans.push(currentSpan);

        return (
            <div className="week-subject-layer">
                {spans.map((span, i) => (
                    <div key={i} className={`subject-ribbon ${span.isBreak ? 'is-break-span' : ''} ${span.subject === 'mixed' ? 'is-mixed-span' : ''}`}
                        style={{ gridColumn: `${span.start + 1} / span ${span.length}`, backgroundColor: span.isBreak ? 'transparent' : (span.subject === 'mixed' ? '#455A64' : `var(--subj-${span.subject})`) }}>
                        {!span.isBreak && <span className="ribbon-label">{span.subject === 'mixed' ? 'Mixed Focus' : (subjectMapDerived[span.subject] || span.subject)}</span>}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="unified-calendar-container">
            <div className="cal-nav-toolbar">
                <button className="nav-icon-btn" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}><span className="material-symbols-outlined">chevron_left</span></button>
                <h2 className="current-month-display">{MONTH_NAMES[viewDate.getMonth()]} {viewDate.getFullYear()}</h2>
                <button className="nav-icon-btn" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}><span className="material-symbols-outlined">chevron_right</span></button>
            </div>
            <div className="cal-grid-frame">
                <div className="cal-weekday-header">{DAY_NAMES.map(d => <div key={d} className="weekday-label">{d}</div>)}</div>
                <div className="cal-weeks-wrapper">
                    {calendarWeeks.map((week, wIdx) => (
                        <div key={wIdx} className="calendar-row">
                            {renderSubjectSpans(week)}
                            <div className="days-interaction-layer">
                                {week.map((day) => {
                                    const hasWork = (day.history?.metrics?.learned_count > 0 || day.history?.metrics?.quiz_count > 0);
                                    const isTargetCell = day.isToday;
                                    const cellContent = (
                                        <div className={`day-cell ${day.isCurrentMonth ? 'in-month' : 'out-of-month'} ${day.isToday ? 'is-today' : ''} ${day.isBreak ? 'is-break' : ''} ${day.isPast ? 'is-past' : ''} ${day.isMasteryGoal ? 'is-mastery-goal' : ''} ${day.isBarExam ? 'is-bar-exam' : ''}`}
                                            onClick={() => day.isCurrentMonth && (!day.isPast || day.isToday) && onToggleBreakDay(day.dateStr)}>

                                            {/* Streak Indicator Dot */}
                                            {day.streakStatus !== 'none' && (
                                            <span
                                                className={`material-symbols-outlined day-streak-indicator ${day.streakStatus}`}
                                                title={day.streakStatus === 'active' ? 'Streak Kept' : 'Frozen'}
                                            >
                                                {day.streakStatus === 'active'
                                                ? 'local_fire_department'
                                                : 'ac_unit'}
                                            </span>
                                            )}


                                            <div className="day-cell-top">
                                                <span className="day-number">{day.dateObj.getDate()}</span>
                                                {day.isMasteryGoal && <span className="milestone-icon goal material-symbols-outlined" title="Mastery Goal">track_changes</span>}
                                                {day.isBarExam && <span className="milestone-icon exam material-symbols-outlined" title="Bar Exam">trophy</span>}
                                                {(day.isPast || day.isToday) && day.history?.metrics && (
                                                    <div className="day-history-stats">
                                                        {day.history.metrics.learned_count > 0 && <span className="stat-pill learn">+{day.history.metrics.learned_count}</span>}
                                                        {day.history.metrics.quiz_count > 0 && <span className="stat-pill quiz">+{day.history.metrics.quiz_count}</span>}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="day-cell-center">
                                                {day.isBreak && !hasWork && <span className="material-symbols-outlined break-indicator-centered">coffee</span>}
                                                {!day.isBreak && isMixMode && !day.isPast && day.isCurrentMonth && (day.projection?.volume > 0 || day.isToday) && (
                                                    <div className="mix-mode-volume"><span className="count">{day.projection?.volume || day.history?.targets?.learn || 0}</span><span className="lbl">RULES</span></div>
                                                )}
                                            </div>
                                            <div className="day-cell-bottom">
                                                {!isMixMode && !day.isBreak && !day.isPast && day.isCurrentMonth && (day.projection?.volume > 0 || day.isToday) && (
                                                    <span className="volume-tag">{day.projection?.volume || day.history?.targets?.learn || 0} RULES</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                    return (
                                        <div key={day.dateStr} style={{position: 'relative'}}>
                                            {isTargetCell ? (
                                                <OnboardingTooltip id="hint_planner_break" seenHints={seenHints} onDismiss={onDismissHint} text="Click any day to toggle a break. We'll automatically adjust your daily volume to compensate." position="top">
                                                    {cellContent}
                                                </OnboardingTooltip>
                                            ) : cellContent}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default CalendarView;